/* ===== Tema „Cloud” — peste structura Custom: poza pe tot ecranul, sticlă, bară plutitoare, „Întreabă” =====
 * Doar aspect + asistentul cu întrebări predefinite (răspunsuri din setările localului, fără AI).
 * Acțiunile asistentului apasă butoanele existente (Program, Locație, WiFi, Rezervare, Meniu, Joacă).
 */
import { getCurrentSettings } from '../services/settings.js?v=82.86';
import { getPublicClientSlug } from '../services/public-client.js?v=82.86';

const CSS_ID = 'sjCloudCssLink';
const LIGHT_CSS_ID = 'sjCloudLightCssLink';
const LIGHT_FONT_ID = 'sjCloudLightFonts';
const FONT_ID = 'sjCloudFonts';
const ASK_ID = 'sjCloudAskBtn';
const HEAD_ID = 'sjCloudCampHead';
const APP_VERSION_QS = (() => {
  try {
    const m = /[?&]v=([0-9.]+)/.exec(import.meta.url);
    if (m) return m[1];
    // În build (esbuild), modulul ajunge într-un chunk fără „?v=” → luăm versiunea din <meta name="app-version">.
    const meta = document.querySelector('meta[name="app-version"]');
    return (meta && meta.content) || '';
  } catch (_) {
    return '';
  }
})();

const ROBOT_SVG =
  '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="7" width="16" height="12" rx="4"/><path d="M12 3v4"/><circle cx="9" cy="13" r="1"/><circle cx="15" cy="13" r="1"/></svg>';

function el(tag, attrs = {}, ...children) {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs || {})) {
    if (v == null || v === false) continue;
    if (k === 'class') n.className = v;
    else if (k === 'text') n.textContent = v;
    else if (k === 'html') n.innerHTML = v;
    else if (k.startsWith('on') && typeof v === 'function') n.addEventListener(k.slice(2), v);
    else n.setAttribute(k, v === true ? '' : String(v));
  }
  for (const c of children.flat()) {
    if (c == null || c === false) continue;
    n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return n;
}

function page() {
  return document.getElementById('clientPage');
}

export function isCloudTemplateActive() {
  return !!page()?.classList.contains('template-cloud');
}

/** Cloud Luminos = Cloud + varianta deschisă (pagina are și clasa template-cloud-light). */
export function isCloudLightActive() {
  return !!page()?.classList.contains('template-cloud-light');
}

function ensureLightAssets() {
  if (!document.getElementById(LIGHT_CSS_ID)) {
    const l = document.createElement('link');
    l.id = LIGHT_CSS_ID;
    l.rel = 'stylesheet';
    l.href = `/css/client-cloud-light.css${APP_VERSION_QS ? `?v=${APP_VERSION_QS}` : ''}`;
    document.head.appendChild(l);
  }
  if (!document.getElementById(LIGHT_FONT_ID)) {
    const f = document.createElement('link');
    f.id = LIGHT_FONT_ID;
    f.rel = 'stylesheet';
    f.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap';
    document.head.appendChild(f);
  }
}

/** Rimga = Cloud întunecat cu verde închis și auriu (pagina are și clasa template-cloud-rimga). */
export function isCloudRimgaActive() {
  return !!page()?.classList.contains('template-cloud-rimga');
}

const RIMGA_CSS_ID = 'sjRimgaCssLink';
const RIMGA_FONT_ID = 'sjRimgaFonts';
const RIMGA_HERO_ID = 'sjRimgaHero';
function ensureRimgaAssets() {
  if (!document.getElementById(RIMGA_CSS_ID)) {
    const l = document.createElement('link');
    l.id = RIMGA_CSS_ID;
    l.rel = 'stylesheet';
    l.href = `/css/client-rimga.css${APP_VERSION_QS ? `?v=${APP_VERSION_QS}` : ''}`;
    // după ce se încarcă stilul, panoul își recalculează locul deasupra barei (altfel bara acoperă Meniu/Recenzie)
    l.onload = () => { placeCloudStage(); setTimeout(placeCloudStage, 300); };
    document.head.appendChild(l);
  }
  if (!document.getElementById(RIMGA_FONT_ID)) {
    const f = document.createElement('link');
    f.id = RIMGA_FONT_ID;
    f.rel = 'stylesheet';
    f.href = 'https://fonts.googleapis.com/css2?family=Inter+Tight:wght@500;600;700;800&display=swap';
    document.head.appendChild(f);
  }
}

const RIMGA_TYPE_LINE = {
  cafenea: 'Cafenea', restaurant: 'Restaurant', pizzerie: 'Pizzerie', 'fast-food': 'Fast-food', cofetarie: 'Cofetărie',
  bar: 'Bar', club: 'Club', frizerie: 'Frizerie', salon: 'Salon', spa: 'Spa', magazin: 'Magazin',
  'service-auto': 'Service auto', 'spalatorie-auto': 'Spălătorie auto', 'sala-fitness': 'Sală de fitness', hotel: 'Hotel'
};

const RO_COUNTIES = new Set(['alba', 'arad', 'arges', 'bacau', 'bihor', 'bistrita-nasaud', 'botosani', 'braila', 'brasov', 'buzau',
  'calarasi', 'caras-severin', 'cluj', 'constanta', 'covasna', 'dambovita', 'dolj', 'galati', 'giurgiu', 'gorj', 'harghita',
  'hunedoara', 'ialomita', 'iasi', 'ilfov', 'maramures', 'mehedinti', 'mures', 'neamt', 'olt', 'prahova', 'salaj', 'satu mare',
  'sibiu', 'suceava', 'teleorman', 'timis', 'tulcea', 'valcea', 'vaslui', 'vrancea', 'romania']);
const foldRo = (x) => String(x || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

/** Orașul din adresă (ultima bucată fără cifre și care nu e județ) — pentru eticheta de sus. */
function rimgaCity(s) {
  const addr = String(s.locationMapAddress || '').trim();
  if (!addr) return '';
  const parts = addr.split(',').map((x) => x.trim()).filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i].replace(/^(jud\.?|județul|judetul)\s+.*/i, '').replace(/\b\d{4,6}\b/g, '').trim();
    if (p && !/\d/.test(p) && p.length <= 30 && !RO_COUNTIES.has(foldRo(p.replace(/^(jud\.?|judetul|județul)\s*/i, '')))) return p;
  }
  return '';
}

/** Numele locației: numele din setări (cel din antet); dacă e numele juridic (SRL…), numele locației. */
function rimgaName(s) {
  const company = String(s.companyName || '').trim();
  const loc = String(s.locationMapName || '').trim();
  const legal = /\b(s\.?\s?r\.?\s?l|s\.?\s?a|p\.?\s?f\.?\s?a|i\.?\s?i)\.?(\s|$)/i.test(company);
  if (company && !legal) return company;
  return loc || company;
}

/** Rețelele sociale lângă nume (copii ale butoanelor din antet; apăsarea merge la butonul original). */
function rimgaSocials() {
  const row = document.getElementById('clientTemplateSocialRow');
  const wrap = el('span', { class: 'sj-rh__social' });
  if (!row) return wrap;
  [...row.querySelectorAll('a, button')].forEach((orig) => {
    if (orig.hidden || orig.style.display === 'none' || getComputedStyle(orig).display === 'none') return;
    const c = orig.cloneNode(true);
    c.removeAttribute('id');
    c.removeAttribute('data-action');
    c.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); orig.click(); });
    wrap.append(c);
  });
  return wrap;
}

let rimgaDotsObs = null;
function syncRimgaHero(settings = {}) {
  const p = page();
  if (!p) return;
  const name = rimgaName(settings);
  const city = rimgaCity(settings);
  const type = RIMGA_TYPE_LINE[String(settings.businessType || '').trim()] || '';
  const tag = String(settings.clientTagline || '').trim();
  const slides = document.querySelectorAll('#clientConnectSlideshow .client-connect-slideshow__dot').length
    || (Array.isArray(settings.bgURLs) ? settings.bgURLs.filter(Boolean).length : 0);
  const bars = el('span', { class: 'sj-rh__bars', 'aria-hidden': 'true' });
  for (let i = 0; i < Math.min(slides, 8); i++) bars.append(el('i', { class: i === 0 ? 'is-on' : '' }));
  const count = el('span', { class: 'sj-rh__count', text: slides > 1 ? `01 / ${String(slides).padStart(2, '0')}` : '' });
  const hero = el('div', { id: RIMGA_HERO_ID, class: 'sj-rh' },
    city ? el('span', { class: 'sj-rh__pill' }, el('i'), el('span', { text: city })) : null,
    name ? el('div', { class: 'sj-rh__row' }, el('h1', { class: 'sj-rh__title', text: name }), rimgaSocials()) : null,
    type ? el('p', { class: 'sj-rh__type', text: type }) : null,
    tag ? el('p', { class: 'sj-rh__tag', text: tag }) : null,
    slides > 1 ? el('div', { class: 'sj-rh__progress' }, bars, count) : null
  );
  const old = document.getElementById(RIMGA_HERO_ID);
  if (old) old.replaceWith(hero);
  else p.appendChild(hero);
  placeCloudStage();
  // urmărim slide-ul activ (punctele ascunse ale slideshow-ului)
  rimgaDotsObs?.disconnect();
  const dots = document.querySelector('#clientConnectSlideshow .client-connect-slideshow__dots');
  if (dots && slides > 1) {
    const upd = () => {
      const all = [...dots.querySelectorAll('.client-connect-slideshow__dot')];
      const idx = Math.max(0, all.findIndex((d) => d.classList.contains('is-active')));
      bars.querySelectorAll('i').forEach((b, i) => b.classList.toggle('is-on', i === idx));
      count.textContent = `${String(idx + 1).padStart(2, '0')} / ${String(all.length).padStart(2, '0')}`;
    };
    rimgaDotsObs = new MutationObserver(upd);
    rimgaDotsObs.observe(dots, { subtree: true, attributes: true, attributeFilter: ['class'] });
    upd();
  }
}

function ensureAssets() {
  if (!document.getElementById(CSS_ID)) {
    const l = document.createElement('link');
    l.id = CSS_ID;
    l.rel = 'stylesheet';
    l.href = `/css/client-cloud.css${APP_VERSION_QS ? `?v=${APP_VERSION_QS}` : ''}`;
    document.head.appendChild(l);
  }
  if (!document.getElementById(FONT_ID)) {
    const f = document.createElement('link');
    f.id = FONT_ID;
    f.rel = 'stylesheet';
    f.href = 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,700;12..96,800&family=DM+Sans:wght@400;500;700&display=swap';
    document.head.appendChild(f);
  }
}

function isVisible(node) {
  if (!node) return false;
  if (node.hidden) return false;
  const st = getComputedStyle(node);
  return st.display !== 'none' && st.visibility !== 'hidden';
}

function clickIfVisible(id) {
  const b = document.getElementById(id);
  if (b && isVisible(b)) {
    b.click();
    return true;
  }
  if (b) {
    b.click();
    return true;
  }
  return false;
}

/* ---------- Campanie: titlu deasupra butonului ---------- */
function syncCampaignHead(settings = {}) {
  const block = document.getElementById('layoutBlockCampaign');
  if (!block) return;
  let head = document.getElementById(HEAD_ID);
  if (!head) {
    head = el('div', { id: HEAD_ID, class: 'sj-cloud-camp-head' },
      el('span', { class: 'sj-cloud-camp-kicker' }),
      el('p', { class: 'sj-cloud-camp-title' })
    );
    block.insertBefore(head, block.firstChild);
  }
  const active = String(settings.campaignStatus || '') === 'active';
  if (!active) {
    head.hidden = true;
    return;
  }
  const prizes = (Array.isArray(settings.prizes) ? settings.prizes : [])
    .map((p) => String(p?.message || '').trim())
    .filter(Boolean);
  const end = String(settings.endDate || '').slice(0, 10);
  const kicker = head.querySelector('.sj-cloud-camp-kicker');
  const title = head.querySelector('.sj-cloud-camp-title');
  kicker.textContent = active ? `Campanie activă${end ? ` · până pe ${roDate(end)}` : ''}` : 'Campanie';
  title.textContent = active
    ? (prizes.length ? `Joacă și câștigă: ${prizes.slice(0, 2).join(' · ')}` : 'Joacă și câștigă')
    : 'Campania începe în curând';
  head.hidden = false;
}

function roDate(iso) {
  const months = ['ian', 'feb', 'mar', 'apr', 'mai', 'iun', 'iul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const [y, m, d] = String(iso).split('-').map(Number);
  return y && m && d ? `${d} ${months[m - 1]}` : iso;
}

/* ---------- Cloud Luminos: cardul activ ca „tichet” negru, deasupra barei ---------- */
const TICKET_CLASS = 'sj-ticket';
let ticketObserver = null;

function shortReward(reward) {
  const r = String(reward || '').trim().replace(/[.!]+$/, '');
  return r.length > 48 ? `${r.slice(0, 46)}…` : r;
}

function ticketContent(kind, s) {
  if (kind === 'loyalty') {
    const ls = s.loyaltySettings || {};
    const required = Math.max(1, parseInt(ls.requiredPurchases, 10) || 6);
    const rawReward = String(ls.rewardText || '').trim().replace(/[.!]+$/, '');
    // „o cafea gratuită” → „La 6 bonuri primești o cafea gratuită”;
    // frază întreagă („La 7 produse cumpărate, primești unul gratuit”) → afișată ca atare
    const isSentence = rawReward.length > 40 || /\d/.test(rawReward) || /primești|primesti/i.test(rawReward);
    const end = String(ls.endDate || '').slice(0, 10);
    return {
      kicker: 'Card de fidelitate',
      side: end ? `până pe ${roDate(end)}` : '',
      title: isSentence ? rawReward : `La ${required} bonuri primești ${rawReward || 'o recompensă'}`,
      cta: '📷 Scanează bonul'
    };
  }
  if (kind === 'event') {
    const em = s.eventModule || {};
    return {
      kicker: 'Eveniment',
      side: '',
      title: String(em.title || '').trim() || 'Eveniment special',
      cta: 'Vezi detalii'
    };
  }
  const prizes = (Array.isArray(s.prizes) ? s.prizes : [])
    .map((p) => String(p?.message || '').trim())
    .filter(Boolean);
  const end = String(s.endDate || '').slice(0, 10);
  const named = String(s.campaignTitle || s.campaignName || '').trim();
  return {
    kicker: 'Bilet norocos',
    side: end ? `până pe ${roDate(end)}` : '',
    title: named || (prizes.length ? `Joacă și câștigă ${shortReward(prizes[0])}` : 'Joacă și câștigă premii'),
    cta: String(s.playButtonLabel || '').trim().slice(0, 28) || 'Încearcă-ți norocul'
  };
}

function syncLightTicket(settings) {
  const card = document.getElementById('clientConnectCampaignCard');
  const pg = page();
  let ticket = card?.querySelector(`.${TICKET_CLASS}`);
  if (!card || !isCloudLightActive()) {
    ticket?.remove();
    ticketObserver?.disconnect();
    ticketObserver = null;
    pg?.style.removeProperty('--cl-ticket-h');
    return;
  }
  const s = settings && Object.keys(settings).length ? settings : (getCurrentSettings() || {});
  const kind = card.dataset.connectModule || 'campaign';
  const c = ticketContent(kind, s);
  if (!ticket) {
    ticket = el('span', { class: TICKET_CLASS, 'aria-hidden': 'true' },
      el('span', { class: 'sj-ticket__notch sj-ticket__notch--l' }),
      el('span', { class: 'sj-ticket__notch sj-ticket__notch--r' }),
      el('span', { class: 'sj-ticket__top' },
        el('span', { class: 'sj-ticket__kicker' }),
        el('span', { class: 'sj-ticket__side' })
      ),
      el('span', { class: 'sj-ticket__title' }),
      el('span', { class: 'sj-ticket__cta' })
    );
    card.appendChild(ticket);
  }
  ticket.dataset.kind = kind;
  ticket.querySelector('.sj-ticket__kicker').textContent = c.kicker;
  ticket.querySelector('.sj-ticket__side').textContent = c.side;
  ticket.querySelector('.sj-ticket__title').textContent = c.title;
  ticket.querySelector('.sj-ticket__cta').textContent = c.cta;
  card.setAttribute('aria-label', `${c.kicker}: ${c.title}. ${c.cta}`);

  // înălțimea reală a tichetului împinge Meniu/Recenzie și pozele în sus
  const setH = () => {
    // offsetHeight = înălțimea reală (nu e afectată de scalarea preview-ului din admin)
    const h = Math.ceil(ticket.offsetHeight);
    if (h > 0) pg?.style.setProperty('--cl-ticket-h', `${h}px`);
  };
  setH();
  if (!ticketObserver && typeof ResizeObserver !== 'undefined') {
    ticketObserver = new ResizeObserver(setH);
    ticketObserver.observe(ticket);
  }
}

/* ---------- Asistentul „Întreabă” ---------- */
function buildQuestions(settings = {}) {
  const s = settings || {};
  const qs = [];
  const name = s.locationMapName || s.companyName || 'Localul';

  // Voucherele clientului (salvate pe telefon) — le folosește singur la casă
  let savedVouchers = 0;
  try {
    const slug = getPublicClientSlug() || 'local';
    const raw = localStorage.getItem(`sj_vouchers_v1:${slug}`);
    savedVouchers = (raw ? JSON.parse(raw) : []).filter((v) => v && !v.redeemedAt).length;
  } catch (_) { /* ignore */ }
  qs.push({
    id: 'voucher',
    direct: true, // deschide direct lista de vouchere, fără pasul de întrebare
    q: savedVouchers ? `🎟️ Voucherele mele (${savedVouchers})` : '🎟️ Am un cod de voucher',
    run: () => { import('./voucher.js?v=82.86').then((m) => m.openVoucherWallet()); return true; }
  });


  if (s.locationHoursEnabled !== false && (s.locationHoursImageURL || document.getElementById('clientLocationHoursFab'))) {
    qs.push({
      id: 'program',
      q: 'Ce program aveți?',
      a: 'Uite programul nostru complet.',
      action: 'Vezi programul',
      run: () => clickIfVisible('clientLocationHoursFab')
    });
  }
  if (s.wifiEnabled && s.wifiSsid) {
    const pass = String(s.wifiPassword || '').trim();
    qs.push({
      id: 'wifi',
      q: 'Care e parola de WiFi?',
      a: `Rețea: ${s.wifiSsid}${pass ? `\nParolă: ${pass}` : '\nFără parolă.'}`,
      action: pass ? 'Copiază parola' : '',
      run: () => {
        try { navigator.clipboard?.writeText(pass); } catch (_) { /* ignore */ }
        return true;
      }
    });
  }
  if (String(s.campaignStatus || '') === 'active') {
    const prizes = (Array.isArray(s.prizes) ? s.prizes : []).map((p) => String(p?.message || '').trim()).filter(Boolean);
    const end = String(s.endDate || '').slice(0, 10);
    qs.push({
      id: 'premii',
      q: 'Ce pot câștiga?',
      a: `${prizes.length ? `Premii: ${prizes.join(', ')}.` : 'Avem o campanie cu premii.'}${end ? `\nCampania ține până pe ${roDate(end)}.` : ''}`,
      action: 'Joacă acum',
      run: () => clickIfVisible('playBtn')
    });
  }
  if (s.reservationEnabled && s.reservationPhone) {
    qs.push({
      id: 'rezerv',
      q: 'Pot rezerva?',
      a: `Da! Sună-ne la ${s.reservationPhone}.`,
      action: 'Sună acum',
      run: () => { window.location.href = `tel:${String(s.reservationPhone).replace(/[^\d+]/g, '')}`; return true; }
    });
  }
  if (s.locationMapEnabled && (s.locationMapUrl || s.locationMapAddress)) {
    qs.push({
      id: 'drum',
      q: 'Cum ajung?',
      a: s.locationMapAddress ? `Ne găsești la ${s.locationMapAddress}.` : `Uite unde ne găsești.`,
      action: 'Deschide harta',
      run: () => clickIfVisible('clientLocationMapFab')
    });
  }
  if (s.menuEnabled) {
    qs.push({
      id: 'meniu',
      q: 'Văd meniul?',
      a: 'Sigur, aici e meniul nostru.',
      action: 'Deschide meniul',
      run: () => clickIfVisible('locationMenuBtn')
    });
  }
  if (s.feedbackEnabled && s.clientFeedbackUrl) {
    qs.push({
      id: 'review',
      q: 'Vreau să las o recenzie',
      a: `Mulțumim! O recenzie ne ajută enorm. ${name} îți mulțumește.`,
      action: 'Lasă o recenzie',
      run: () => clickIfVisible('feedbackBtn')
    });
  }
  return qs;
}

let sheet = null;

function closeSheet() {
  sheet?.remove();
  sheet = null;
}

export function openCloudAssistant() {
  closeSheet();
  const s = getCurrentSettings() || {};
  const name = s.locationMapName || s.companyName || 'localul';
  const qs = buildQuestions(s);
  const body = el('div', { class: 'sj-cloud-sheet__body', 'aria-live': 'polite' },
    el('div', { class: 'sj-cloud-msg sj-cloud-msg--bot', text: qs.length ? 'Salut! Cu ce te pot ajuta? Alege o întrebare.' : 'Salut! Momentan nu am informații de afișat.' })
  );
  const chips = el('div', { class: 'sj-cloud-sheet__chips' });
  const ask = (item, chip) => {
    chips.querySelectorAll('.sj-cloud-chip').forEach((c) => c.classList.remove('is-on'));
    chip.classList.add('is-on');
    body.append(el('div', { class: 'sj-cloud-msg sj-cloud-msg--me', text: item.q }));
    body.append(el('div', { class: 'sj-cloud-msg sj-cloud-msg--bot', text: item.a }));
    if (item.action) {
      body.append(el('button', {
        type: 'button', class: 'sj-cloud-msg__action',
        onclick: () => {
          const keepOpen = item.id === 'wifi';
          item.run?.();
          if (keepOpen) return;
          closeSheet();
        }
      }, item.action));
    }
    body.scrollTop = body.scrollHeight;
  };
  qs.forEach((item) => {
    const chip = el('button', { type: 'button', class: 'sj-cloud-chip' }, item.q);
    chip.addEventListener('click', () => {
      if (item.direct) { closeSheet(); item.run?.(); return; }
      ask(item, chip);
    });
    chips.append(chip);
  });

  sheet = el('div', { id: 'sjCloudSheet', class: 'sj-cloud-sheet-backdrop', role: 'dialog', 'aria-modal': 'true', 'aria-label': `Joy · ${name}` },
    el('div', { class: 'sj-cloud-sheet' },
      el('span', { class: 'sj-cloud-sheet__grip', 'aria-hidden': 'true' }),
      el('div', { class: 'sj-cloud-sheet__head' },
        el('div', { class: 'sj-cloud-sheet__avatar', html: ROBOT_SVG }),
        el('div', {},
          el('p', { class: 'sj-cloud-sheet__title', text: `Joy · ${name}` }),
          el('p', { class: 'sj-cloud-sheet__sub', text: 'Răspunsuri din informațiile localului' })
        ),
        el('button', { type: 'button', class: 'sj-cloud-sheet__close', 'aria-label': 'Închide', onclick: closeSheet }, '✕')
      ),
      body,
      chips
    )
  );
  sheet.addEventListener('click', (e) => { if (e.target === sheet) closeSheet(); });
  document.body.appendChild(sheet);
}

/** Butonul „Joy” din bara de jos (asistentul cu întrebări rapide + voucherele clientului). Folosit și de Afiș/Tablă/Glass. */
export function ensureAskButton() {
  const bar = document.getElementById('clientFabBar');
  if (!bar) return;
  let btn = document.getElementById(ASK_ID);
  if (!btn) {
    btn = el('button', { type: 'button', id: ASK_ID, class: 'client-fab-pill client-premium-fab', 'aria-label': 'Joy' },
      el('span', { class: 'client-premium-fab__icon', html: ROBOT_SVG }),
      el('span', { class: 'client-premium-fab__text' }, el('span', { class: 'client-fab-pill__label client-premium-fab__title', text: 'Joy' }))
    );
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      openCloudAssistant();
    });
  }
  if (bar.firstChild !== btn) bar.insertBefore(btn, bar.firstChild);
  // Voucherele stau în Joy — fără butonul plutitor peste bara de jos
  document.getElementById('sjVoucherWalletBtn')?.remove();
}

let customSyncTimer = null;

/* ---------- „Ofertele săptămânii” (secțiunea editabilă de comerciant) — card premium în Cloud Dark ---------- */
const OFFERS_ID = 'sjCloudOffers';

function offersData(settings = {}) {
  const g = settings.clientPromoGallery || {};
  if (g.enabled !== true) return null;
  const images = (Array.isArray(g.images) ? g.images : []).map((u) => String(u || '').trim()).filter(Boolean);
  const title = String(g.title || '').trim();
  if (!images.length && !title) return null;
  return { title: title || 'Ofertele săptămânii', images, labels: Array.isArray(g.labels) ? g.labels : [] };
}

function syncCloudOffers(settings = {}) {
  const p = page();
  const existing = document.getElementById(OFFERS_ID);
  const data = isCloudTemplateActive() && !isCloudLightActive() ? offersData(settings) : null;
  if (!p || !data) {
    existing?.remove();
    p?.classList.remove('sj-has-offers');
    return;
  }
  import('../utils/url.js?v=82.86').then(({ sanitizeImageSrc }) => {
    const thumbs = data.images
      .slice(0, 3)
      .map((u) => sanitizeImageSrc(u))
      .filter(Boolean)
      .map((src, i) => el('span', { class: 'sj-offers__thumb', style: `--i:${i}` }, el('img', { src, alt: '', loading: 'lazy', decoding: 'async' })));
    const n = data.images.length;
    const named = (Array.isArray(data.labels) ? data.labels : []).map((l) => String(l || '').trim()).filter(Boolean);
    const sub = named.length ? named.slice(0, 3).join(' · ') + (named.length > 3 ? ' …' : '')
      : n > 1 ? `${n} oferte · atinge să le vezi` : n === 1 ? 'Atinge să vezi oferta' : 'Atinge pentru detalii';
    const card = el(
      'button',
      {
        type: 'button',
        id: OFFERS_ID,
        class: 'sj-offers',
        'aria-label': data.title,
        onclick: () => {
          import('./client-promo-gallery.js?v=82.86')
            .then((m) => m.openClientPromoGalleryOverlay?.())
            .catch(() => {});
        }
      },
      el('span', { class: `sj-offers__stack${thumbs.length ? '' : ' is-empty'}` }, thumbs.length ? thumbs : el('span', { class: 'sj-offers__icon', text: '🔥' })),
      el('span', { class: 'sj-offers__text' },
        el('span', { class: 'sj-offers__kicker', text: 'Oferte' }),
        el('b', { class: 'sj-offers__title', text: data.title }),
        el('span', { class: 'sj-offers__sub', text: sub })
      ),
      el('span', { class: 'sj-offers__go', 'aria-hidden': 'true', html: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m9 6 6 6-6 6"/></svg>' })
    );
    const old = document.getElementById(OFFERS_ID);
    if (old) old.replaceWith(card);
    else p.appendChild(card);
    p.classList.add('sj-has-offers');
    placeCloudOffers();
    setTimeout(placeCloudOffers, 400);
  }).catch(() => {});
}

/** Cardul stă imediat deasupra celui mai de sus bloc de jos (modul activ sau Meniu/Recenzie). */
function placeCloudOffers() {
  const p = page();
  const card = document.getElementById(OFFERS_ID);
  if (!p || !card) return;
  const pr = p.getBoundingClientRect();
  const blocks = ['clientConnectPromoRow', 'clientPremiumSectionDuo']
    .map((id) => document.getElementById(id))
    .filter((n) => n && !n.hidden && n.offsetHeight > 0)
    .map((n) => n.getBoundingClientRect().top);
  if (!blocks.length) return;
  const top = Math.min(...blocks);
  card.style.bottom = `${Math.max(0, Math.round(pr.bottom - top + 10))}px`;
}
if (typeof window !== 'undefined') {
  window.addEventListener('resize', () => requestAnimationFrame(placeCloudOffers));
}

/* ---------- Cloud Dark · varianta A: carduri mari (secțiunea editabilă + activitatea activă), Meniu/Recenzie ---------- */
const STAGE_ID = 'sjCloudA';
const RO_MONTHS = ['ian', 'feb', 'mar', 'apr', 'mai', 'iun', 'iul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const RO_DAYS = ['DUM', 'LUN', 'MAR', 'MIE', 'JOI', 'VIN', 'SÂM'];
const CHEV = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>';

function tileShown(id) {
  const n = document.getElementById(id);
  return !!n && !n.hidden && getComputedStyle(n).display !== 'none';
}

function syncCloudStage(settings = {}) {
  const p = page();
  if (!p) return;
  document.getElementById(OFFERS_ID)?.remove();
  p.classList.remove('sj-has-offers');
  import('../utils/url.js?v=82.86').then(({ sanitizeImageSrc }) => {
    const cards = [];

    // 1) Secțiunea editabilă: DOAR ce a scris comerciantul (titlu) + poza; „Vezi detalii”
    const g = settings.clientPromoGallery || {};
    const gImgs = (Array.isArray(g.images) ? g.images : []).map((u) => sanitizeImageSrc(String(u || '').trim())).filter(Boolean);
    const gTitle = String(g.title || '').trim();
    if (g.enabled === true && (gImgs.length || gTitle)) {
      cards.push(el('button', {
        type: 'button',
        class: `sj-a__card sj-a__card--offers${gImgs.length ? ' has-img' : ''}`,
        'aria-label': gTitle || 'Vezi detalii',
        onclick: () => import('./client-promo-gallery.js?v=82.86').then((m) => {
          if (m.openClientPromoGalleryOverlay?.()) return;
          // fereastra galeriei nu era pregătită (ordinea încărcării) → o pregătim acum și o deschidem
          m.applyClientPromoGalleryUI?.(getCurrentSettings() || settings);
          m.openClientPromoGalleryOverlay?.();
        }).catch(() => {})
      },
        gImgs.length ? el('img', { class: 'sj-a__img', src: gImgs[0], alt: '', loading: 'lazy', decoding: 'async' }) : null,
        el('span', { class: 'sj-a__body' },
          gTitle ? el('b', { class: 'sj-a__title', text: gTitle }) : null,
          el('span', { class: 'sj-a__more', html: (() => {
            // numele pozelor (ex. „Desert · Băuturi”) în loc de „Vezi detalii”, când comerciantul le-a dat
            const named = (Array.isArray(g.labels) ? g.labels : []).map((l) => String(l || '').trim()).filter(Boolean);
            const t = named.length ? named.slice(0, 3).join(' · ') + (named.length > 3 ? ' …' : '') : 'Vezi detalii';
            return `${t.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]))} ${CHEV}`;
          })() })
        )
      ));
    }

    // 2) Activitatea activă (campanie / fidelizare / eveniment) — textele cardului existent
    const camp = document.getElementById('clientConnectCampaignCard');
    const row = document.getElementById('clientConnectPromoRow');
    const kind = camp?.dataset.connectModule;
    if (row && !row.hidden && camp && !camp.hidden && kind) {
      const badge = camp.querySelector('.client-custom-module-card__badge')?.textContent?.trim() || '';
      const title = camp.querySelector('.client-custom-module-card__title')?.textContent?.trim() || '';
      const desc = camp.querySelector('.client-custom-module-card__desc')?.textContent?.trim() || '';
      const kicker = kind === 'event' ? 'Eveniment' : kind === 'loyalty' ? 'Card de fidelitate' : (badge || 'Campanie');
      // Campanie cu afiș propriu (poza premiului pusă de comerciant) → afișul umple cardul, care pulsează discret.
      // Pe cardul de campanie nu mai scriem „Joacă acum”: titlul („Înscrie-te!”) e deja îndemnul.
      const isCampaign = kind !== 'event' && kind !== 'loyalty';
      const poster = isCampaign
        ? (Array.isArray(settings.prizes) ? settings.prizes : [])
          .map((pz) => String(pz?.image || '').trim())
          .find((u) => u && !/\/img\/rewards\//.test(u) && !u.startsWith('data:'))
        : '';
      const posterSrc = poster ? sanitizeImageSrc(poster) : '';
      const more = kind === 'event' ? 'Detalii' : kind === 'loyalty' ? 'Scanează bonul' : '';
      let top = null;
      const em = settings.eventModule || {};
      if (kind === 'event' && /^\d{4}-\d{2}-\d{2}$/.test(String(em.eventDate || ''))) {
        const [y, m, d] = String(em.eventDate).split('-').map(Number);
        const wd = RO_DAYS[new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay()];
        top = el('span', { class: 'sj-a__top' },
          el('span', { class: 'sj-a__date' },
            el('span', { text: wd }), el('b', { text: String(d) }), el('span', { text: RO_MONTHS[m - 1].toUpperCase() })),
          /^\d{2}:\d{2}$/.test(String(em.eventTime || '')) ? el('span', { class: 'sj-a__time', text: em.eventTime }) : null
        );
      } else if (posterSrc) {
        top = null;
      } else {
        const icon = camp.querySelector('.client-custom-module-card__icon')?.textContent?.trim() || '';
        top = el('span', { class: 'sj-a__top' }, el('span', { class: 'sj-a__badge', text: icon || '★' }));
      }
      cards.push(el('button', {
        type: 'button',
        class: `sj-a__card sj-a__card--module is-${kind}${posterSrc ? ' has-img has-poster' : ''}`,
        'aria-label': title || desc || kicker,
        onclick: () => camp.click()
      },
        posterSrc ? el('img', { class: 'sj-a__img sj-a__poster', src: posterSrc, alt: '', decoding: 'async' }) : null,
        posterSrc ? el('span', { class: 'sj-a__shine', 'aria-hidden': 'true' }) : null,
        top,
        el('span', { class: 'sj-a__body' },
          el('span', { class: 'sj-a__kicker' }, posterSrc ? el('i', { class: 'sj-a__live', 'aria-hidden': 'true' }) : null, kicker),
          el('b', { class: 'sj-a__title', text: title || desc || kicker }),
          more ? el('span', { class: 'sj-a__more', html: `${more} ${CHEV}` }) : null
        )
      ));
    }

    // 3) Meniu / Recenzie
    const duo = [];
    if (tileShown('layoutBlockMenu')) {
      duo.push(el('button', { type: 'button', class: 'sj-a__act sj-a__act--gold', onclick: () => document.getElementById('locationMenuBtn')?.click() },
        el('span', { html: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h10"/></svg>' }),
        el('span', { class: 'sj-a__actx' }, el('b', { text: String(settings.menuLabel || '').trim() || 'Meniu' }), el('small', { text: 'Vezi detalii' })),
        isCloudRimgaActive() ? el('span', { class: 'sj-a__arrow', 'aria-hidden': 'true', text: '→' }) : null));
    }
    if (tileShown('layoutBlockReview')) {
      duo.push(el('button', { type: 'button', class: 'sj-a__act', onclick: () => document.getElementById('feedbackBtn')?.click() },
        el('span', { html: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFD27A" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z"/></svg>' }),
        el('span', { class: 'sj-a__actx' }, el('b', { text: String(settings.feedbackButtonLabel || '').trim() || 'Recenzie' }), el('small', { text: isCloudRimgaActive() ? 'Spune-ne părerea ta' : 'Google' })),
        isCloudRimgaActive() ? el('span', { class: 'sj-a__arrow', 'aria-hidden': 'true', text: '→' }) : null));
    }

    const rimga = isCloudRimgaActive();
    const stage = el('div', { id: STAGE_ID, class: 'sj-a' },
      cards.length ? el('div', { class: `sj-a__cards${cards.length === 1 ? ' is-single' : ''}` }, cards) : null,
      rimga && duo.length ? el('div', { class: 'sj-a__label' }, el('span', { text: 'Descoperă mai mult' })) : null,
      duo.length ? el('div', { class: `sj-a__duo${duo.length === 1 ? ' is-single' : ''}` }, duo) : null
    );
    const old = document.getElementById(STAGE_ID);
    if (old) old.replaceWith(stage);
    else p.appendChild(stage);
    p.classList.add('sj-cloud-a');
    placeCloudStage();
    setTimeout(placeCloudStage, 350);
  }).catch(() => {});
}

/** Panoul stă deasupra barei; poza de sus se oprește unde începe panoul. */
function placeCloudStage() {
  const p = page();
  const stage = document.getElementById(STAGE_ID);
  if (!p || !stage) return;
  const bar = document.getElementById('clientPremiumBottomDock') || document.getElementById('clientFabBar');
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const barTop = bar && bar.offsetHeight ? bar.getBoundingClientRect().top : vh - 90;
  stage.style.paddingBottom = `${Math.max(0, Math.round(vh - barTop + 12))}px`;
  const h = stage.offsetHeight;
  p.style.setProperty('--sj-a-h', `${h}px`);
}
if (typeof window !== 'undefined') {
  window.addEventListener('resize', () => requestAnimationFrame(placeCloudStage));
}

export function applyCloudTemplateDom(settings) {
  if (!isCloudTemplateActive()) return;
  const s = settings && Object.keys(settings).length ? settings : (getCurrentSettings() || {});
  ensureAssets();
  document.body.classList.add('sj-cloud-active');
  if (isCloudLightActive()) {
    ensureLightAssets();
    document.body.classList.add('sj-cloud-light');
  } else {
    document.body.classList.remove('sj-cloud-light');
  }
  if (isCloudRimgaActive()) {
    ensureRimgaAssets();
    document.body.classList.add('sj-cloud-rimga');
  } else {
    document.body.classList.remove('sj-cloud-rimga');
    document.getElementById(RIMGA_HERO_ID)?.remove();
  }
  ensureAskButton();
  syncCampaignHead(s);
  // Cloud folosește structura Custom: randăm explicit hero-ul + antetul Custom
  // (pe pagina publică sincronizarea Custom poate rula înainte să existe clasa de temă).
  clearTimeout(customSyncTimer);
  customSyncTimer = setTimeout(() => {
    if (!isCloudTemplateActive()) return;
    import('./client-custom-template.js?v=82.86')
      .then((m) => {
        m.syncCustomTemplate?.(getCurrentSettings() || s);
        m.finalizeCustomDuoChrome?.(getCurrentSettings() || s);
        ensureAskButton();
        syncLightTicket(getCurrentSettings() || s);
        if (isCloudLightActive()) {
          document.getElementById(STAGE_ID)?.remove();
          page()?.classList.remove('sj-cloud-a');
          syncCloudOffers(getCurrentSettings() || s);
        }
        else syncCloudStage(getCurrentSettings() || s);
        if (isCloudRimgaActive()) {
          syncRimgaHero(getCurrentSettings() || s);
          setTimeout(() => isCloudRimgaActive() && syncRimgaHero(getCurrentSettings() || s), 600);
        }
      })
      .catch(() => {});
  }, 30);
}

export function restoreCloudTemplateDom() {
  document.body.classList.remove('sj-cloud-active', 'sj-cloud-light', 'sj-cloud-rimga');
  document.getElementById(RIMGA_HERO_ID)?.remove();
  rimgaDotsObs?.disconnect();
  rimgaDotsObs = null;
  document.getElementById(ASK_ID)?.remove();
  document.getElementById(HEAD_ID)?.remove();
  document.getElementById(OFFERS_ID)?.remove();
  page()?.classList.remove('sj-has-offers');
  document.getElementById(STAGE_ID)?.remove();
  page()?.classList.remove('sj-cloud-a');
  page()?.style.removeProperty('--sj-a-h');
  document.querySelector(`#clientConnectCampaignCard .${TICKET_CLASS}`)?.remove();
  ticketObserver?.disconnect();
  ticketObserver = null;
  page()?.style.removeProperty('--cl-ticket-h');
  closeSheet();
}
